import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, Tag, FileText, Clock, User, Zap, Shield, Eye, BarChart, Download, Figma, Link2 } from 'lucide-react';

const DigitalAssetDetail = () => {
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState('description');
  const [asset] = useState({
    _id: id,
    title: "Modern UI Kit",
    description: "Professional Figma UI components library with 50+ screens, 150+ components and auto-layout system",
    category: "Figma",
    price: 49.99,
    discountPrice: 39.99,
    fileUrl: ["https://example.com/files/ui-kit.fig"],
    previewUrl: "https://via.placeholder.com/800x600",
    tags: ["UI Kit", "Figma", "Components", "Modern"],
    version: "2.1.0",
    features: ["Auto-layout", "Dark/Light mode", "Responsive", "Free updates"],
    status: "active",
    totalSales: 142,
    totalViews: 2560,
    featured: true,
    visibility: "public",
    createdAt: "2024-01-15",
    updatedAt: "2024-03-20",
    seller: "12345",
    submittedBy: "12345"
  });

  const renderTabContent = () => {
    switch(activeTab) {
      case 'description':
        return (
          <div className="space-y-6">
            <p className="text-navy-600 leading-relaxed">{asset.description}</p>
            <div className="grid md:grid-cols-2 gap-4">
              {asset.features.map((feature, index) => (
                <div key={index} className="flex items-center p-3 bg-navy-50 rounded-lg">
                  <Zap size={16} className="text-navy-600 mr-2" />
                  <span className="text-navy-700">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        );
      
      case 'files':
        return (
          <div className="space-y-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-navy-900">File Downloads</h3>
              {asset.fileUrl.map((url, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-navy-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <FileText size={18} className="text-navy-600" />
                    <span className="text-navy-700 break-all">{url}</span>
                  </div>
                  <button className="px-4 py-2 bg-navy-900 text-white rounded-lg hover:bg-navy-800 transition-colors">
                    Download
                  </button>
                </div>
              ))}
            </div>

            {asset.previewUrl && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-navy-900">Preview</h3>
                <div className="border-2 border-navy-100 rounded-xl overflow-hidden">
                  <img 
                    src={asset.previewUrl} 
                    alt="Preview" 
                    className="w-full h-64 object-cover"
                  />
                  <div className="p-4 bg-white border-t border-navy-100">
                    <a 
                      href={asset.previewUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-navy-600 hover:text-navy-800"
                    >
                      <Link2 size={16} className="mr-2" />
                      Open Full Preview
                    </a>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      
      case 'details':
        return (
          <div className="grid md:grid-cols-2 gap-6">
            <DetailItem icon={FileText} label="Version" value={asset.version} />
            <DetailItem icon={Clock} label="Last Updated" value={new Date(asset.updatedAt).toLocaleDateString()} />
            <DetailItem icon={User} label="Seller" value={asset.seller} />
            <DetailItem icon={Shield} label="Visibility" value={asset.visibility} />
          </div>
        );
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-4">
      <Link to="/assets" className="mb-4 inline-flex items-center text-navy-600 hover:text-navy-800">
        <ArrowLeft size={18} className="mr-2" />
        Back to Assets
      </Link>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl border border-navy-100 p-6">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <h1 className="text-3xl font-bold text-navy-900">{asset.title}</h1>
              <span className={`px-3 py-1 rounded-full text-sm ${
                asset.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              }`}>
                {asset.status}
              </span>
            </div>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mb-6">
              {asset.tags.map((tag, index) => (
                <span key={index} className="px-3 py-1 bg-navy-50 text-navy-700 rounded-full text-sm flex items-center">
                  <Tag size={14} className="mr-2" />
                  {tag}
                </span>
              ))}
            </div>

            {/* Tabs Navigation */}
            <div className="flex border-b border-navy-100 mb-6">
              {['description', 'files', 'details'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab 
                      ? 'border-navy-900 text-navy-900' 
                      : 'border-transparent text-navy-600 hover:text-navy-900'
                  }`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="min-h-[400px]">
              {renderTabContent()}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Pricing Card */}
          <div className="bg-white rounded-xl border border-navy-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                {asset.discountPrice ? (
                  <>
                    <span className="text-3xl font-bold text-navy-900">${asset.discountPrice}</span>
                    <span className="ml-2 line-through text-navy-400">${asset.price}</span>
                  </>
                ) : (
                  <span className="text-3xl font-bold text-navy-900">${asset.price}</span>
                )}
              </div>
            </div>

            <button className="w-full bg-navy-900 text-white py-3 rounded-lg hover:bg-navy-800 transition-colors flex items-center justify-center">
              <ShoppingCart size={18} className="mr-2" />
              Purchase Now
            </button>
          </div>

          {/* Stats Card */}
          <div className="bg-white rounded-xl border border-navy-100 p-6">
            <h3 className="text-lg font-semibold text-navy-900 mb-4">Statistics</h3>
            <div className="space-y-3">
              <StatItem icon={BarChart} label="Total Sales" value={asset.totalSales} />
              <StatItem icon={Eye} label="Total Views" value={asset.totalViews} />
              <StatItem icon={Clock} label="Created" value={new Date(asset.createdAt).toLocaleDateString()} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Componentes auxiliares
const DetailItem = ({ icon: Icon, label, value }) => (
  <div className="flex items-start space-x-4 p-4 bg-navy-50 rounded-lg">
    <Icon size={20} className="text-navy-600 mt-1" />
    <div>
      <div className="text-sm text-navy-600">{label}</div>
      <div className="text-navy-900 font-medium">{value}</div>
    </div>
  </div>
);

const StatItem = ({ icon: Icon, label, value }) => (
  <div className="flex items-center justify-between p-3 hover:bg-navy-50 rounded-lg">
    <div className="flex items-center space-x-2">
      <Icon size={16} className="text-navy-600" />
      <span className="text-navy-600">{label}</span>
    </div>
    <span className="text-navy-900 font-medium">{value}</span>
  </div>
);

export default DigitalAssetDetail;